import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IzzyMetadataEngine } from 'mj_generatedentities';
import { mjBizAppsCommonOrganizationEntity } from '@mj-biz-apps/common-entities';

/**
 * Tree node for hierarchical organization display
 */
interface OrgTreeNode {
  org: mjBizAppsCommonOrganizationEntity;
  children: OrgTreeNode[];
  expanded: boolean;
  depth: number;
  hasChildren: boolean;
}

/**
 * A beautiful tree-based organization selector dropdown component.
 *
 * Usage:
 * ```html
 * <izzy-org-selector
 *   [(ngModel)]="selectedOrgId"
 *   [placeholder]="'Select organization'"
 *   [showChannelCount]="true"
 *   (orgSelected)="onOrgSelected($event)">
 * </izzy-org-selector>
 * ```
 */
@Component({
  standalone: false,
  selector: 'izzy-org-selector',
  templateUrl: './org-selector.component.html',
  styleUrls: ['./org-selector.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrgSelectorComponent),
      multi: true
    }
  ]
})
export class OrgSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  // ========== Inputs ==========
  @Input() placeholder: string = 'Select organization';
  @Input() showChannelCount: boolean = true;
  @Input() showSubOrgCount: boolean = true;
  @Input() disabled: boolean = false;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  /** Theme variant: 'light' for standard forms, 'dark' for dark nav sidebars */
  @Input() theme: 'light' | 'dark' = 'light';
  /** When true, adds an "All Organizations" option at the top of the list */
  @Input() allowAll: boolean = false;
  /** Label for the "All" option when allowAll is true */
  @Input() allLabel: string = 'All Organizations';

  // ========== Outputs ==========
  /** Emits the selected org, or null when "All Organizations" is selected */
  @Output() orgSelected = new EventEmitter<mjBizAppsCommonOrganizationEntity | null>();

  // ========== Data ==========
  protected organizations: mjBizAppsCommonOrganizationEntity[] = [];
  protected orgTree: OrgTreeNode[] = [];

  // ========== State ==========
  protected isOpen: boolean = false;
  protected searchQuery: string = '';
  protected selectedOrg: mjBizAppsCommonOrganizationEntity | null = null;
  protected isAllSelected: boolean = false;
  private selectedOrgId: string | null = null;

  // ========== Dropdown Position (for fixed positioning) ==========
  protected dropdownStyle: { [key: string]: string } = {};

  // ========== ControlValueAccessor ==========
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef) {}

  async ngOnInit(): Promise<void> {
    await this.loadOrganizations();
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // ========== ControlValueAccessor Implementation ==========

  writeValue(value: string | null): void {
    this.selectedOrgId = value;
    if (value === null && this.allowAll) {
      // null means "All Organizations" when allowAll is enabled
      this.isAllSelected = true;
      this.selectedOrg = null;
    } else if (value && this.organizations.length > 0) {
      this.isAllSelected = false;
      this.selectedOrg = this.organizations.find(o => o.ID === value) || null;
    } else {
      this.isAllSelected = false;
      this.selectedOrg = null;
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // ========== Data Loading ==========

  /**
   * Public method to refresh the organization list from the engine cache.
   * Call this after creating/deleting organizations to update the dropdown.
   * Assumes the caller has already called IzzyMetadataEngine.Instance.Config(true) to refresh the cache.
   */
  async refresh(): Promise<void> {
    const engine = IzzyMetadataEngine.Instance;
    const currentContactId = engine.CurrentContactID;

    console.log('OrgSelector.refresh(): Re-reading from engine cache');
    console.log('OrgSelector.refresh(): OrganizationContacts count:', engine.OrganizationContacts?.length);

    if (currentContactId) {
      this.organizations = engine.GetUserOrganizations(currentContactId);
    } else {
      this.organizations = engine.Organizations;
    }

    console.log('OrgSelector.refresh(): Organizations after refresh:', this.organizations.length, this.organizations.map(o => o.Name));

    this.buildOrgTree();

    // Preserve current selection if still valid
    if (this.selectedOrgId) {
      this.selectedOrg = this.organizations.find(o => o.ID === this.selectedOrgId) || null;
    }
  }

  private async loadOrganizations(): Promise<void> {
    try {
      await IzzyMetadataEngine.Instance.Config(false);
      const engine = IzzyMetadataEngine.Instance;
      const currentContactId = engine.CurrentContactID;

      // Debug logging
      console.log('OrgSelector: CurrentContactID:', currentContactId);
      console.log('OrgSelector: Total organizations in engine:', engine.Organizations.length);
      console.log('OrgSelector: Total OrganizationContacts loaded:', engine.OrganizationContacts.length);

      if (currentContactId) {
        // Filter organizations by user access (v2.3 - Security)
        const userOrgContacts = engine.OrganizationContacts.filter(
          oc => oc.PersonID === currentContactId && oc.Status === 'Active'
        );
        console.log('OrgSelector: OrganizationContacts for current user:', userOrgContacts.length, userOrgContacts);

        this.organizations = engine.GetUserOrganizations(currentContactId);
        console.log('OrgSelector: Filtered organizations for user:', this.organizations.length, this.organizations.map(o => o.Name));

        if (this.organizations.length === 0) {
          console.warn('OrgSelector: Current user has no organization access');
        }
      } else {
        // Fallback: No current contact - load all organizations
        // This handles system accounts or edge cases
        console.warn('OrgSelector: No current contact found, loading all organizations');
        this.organizations = engine.Organizations;
      }

      this.buildOrgTree();

      // Apply initial selection if set before data loaded
      if (this.selectedOrgId) {
        this.selectedOrg = this.organizations.find(o => o.ID === this.selectedOrgId) || null;
      }
    } catch (error) {
      console.error('OrgSelector: Error loading organizations:', error);
    }
  }

  // ========== Tree Building ==========

  private buildOrgTree(): void {
    const orgMap = new Map<string, OrgTreeNode>();

    // First pass: create nodes for all orgs
    for (const org of this.organizations) {
      orgMap.set(org.ID, {
        org,
        children: [],
        expanded: true,
        depth: 0,
        hasChildren: false
      });
    }

    // Second pass: build parent-child relationships
    const rootNodes: OrgTreeNode[] = [];
    for (const org of this.organizations) {
      const node = orgMap.get(org.ID)!;
      const parentId = org.ParentID;

      if (parentId && orgMap.has(parentId)) {
        const parentNode = orgMap.get(parentId)!;
        parentNode.children.push(node);
        parentNode.hasChildren = true;
      } else {
        rootNodes.push(node);
      }
    }

    // Third pass: calculate depths
    const setDepths = (nodes: OrgTreeNode[], depth: number) => {
      for (const node of nodes) {
        node.depth = depth;
        setDepths(node.children, depth + 1);
      }
    };
    setDepths(rootNodes, 0);

    // Sort children by name at each level
    const sortChildren = (nodes: OrgTreeNode[]) => {
      nodes.sort((a, b) => a.org.Name.localeCompare(b.org.Name));
      for (const node of nodes) {
        sortChildren(node.children);
      }
    };
    sortChildren(rootNodes);

    this.orgTree = rootNodes;
  }

  // ========== Dropdown Control ==========

  toggleDropdown(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchQuery = '';
      this.onTouched();
      this.calculateDropdownPosition();
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  /**
   * Programmatically open the dropdown.
   * Useful for auto-opening after expanding a collapsed nav.
   */
  openDropdown(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.searchQuery = '';
    this.onTouched();
    // Use setTimeout to ensure the DOM has updated before calculating position
    setTimeout(() => this.calculateDropdownPosition(), 0);
  }

  /**
   * Calculate dropdown position for fixed positioning.
   * This ensures the dropdown is not clipped by overflow:hidden containers.
   */
  private calculateDropdownPosition(): void {
    const trigger = this.elementRef.nativeElement.querySelector('.selector-trigger');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const dropdownHeight = 380; // approximate max height
    const viewportHeight = window.innerHeight;

    // Check if dropdown would overflow bottom of viewport
    const spaceBelow = viewportHeight - rect.bottom;
    const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    this.dropdownStyle = {
      position: 'fixed',
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      minWidth: '280px'
    };

    if (openAbove) {
      this.dropdownStyle['bottom'] = `${viewportHeight - rect.top + 4}px`;
    } else {
      this.dropdownStyle['top'] = `${rect.bottom + 4}px`;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.closeDropdown();
  }

  // ========== Selection ==========

  selectOrg(org: mjBizAppsCommonOrganizationEntity, event?: Event): void {
    event?.stopPropagation();
    this.selectedOrg = org;
    this.selectedOrgId = org.ID;
    this.isAllSelected = false;
    this.onChange(org.ID);
    this.orgSelected.emit(org);
    this.closeDropdown();
  }

  selectAll(event?: Event): void {
    event?.stopPropagation();
    this.selectedOrg = null;
    this.selectedOrgId = null;
    this.isAllSelected = true;
    this.onChange(null);
    this.orgSelected.emit(null);
    this.closeDropdown();
  }

  // ========== Tree Navigation ==========

  toggleExpand(node: OrgTreeNode, event: Event): void {
    event.stopPropagation();
    node.expanded = !node.expanded;
  }

  get flattenedTreeNodes(): OrgTreeNode[] {
    if (this.searchQuery.trim()) {
      return this.getFilteredNodes();
    }

    const result: OrgTreeNode[] = [];
    const flatten = (nodes: OrgTreeNode[]) => {
      for (const node of nodes) {
        result.push(node);
        if (node.expanded && node.children.length > 0) {
          flatten(node.children);
        }
      }
    };
    flatten(this.orgTree);
    return result;
  }

  private getFilteredNodes(): OrgTreeNode[] {
    const query = this.searchQuery.toLowerCase().trim();
    const result: OrgTreeNode[] = [];

    const matchesSearch = (org: mjBizAppsCommonOrganizationEntity): boolean => {
      return org.Name.toLowerCase().includes(query);
    };

    // Flatten all nodes when searching (ignore expanded state)
    const flatten = (nodes: OrgTreeNode[]) => {
      for (const node of nodes) {
        if (matchesSearch(node.org)) {
          result.push(node);
        }
        flatten(node.children);
      }
    };
    flatten(this.orgTree);
    return result;
  }

  // ========== Display Helpers ==========

  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getChannelCount(org: mjBizAppsCommonOrganizationEntity): number {
    return IzzyMetadataEngine.Instance.Channels?.filter(c => c.OrganizationID === org.ID).length || 0;
  }

  getDescendantCount(node: OrgTreeNode): number {
    let count = 0;
    const countDescendants = (n: OrgTreeNode) => {
      count += n.children.length;
      for (const child of n.children) {
        countDescendants(child);
      }
    };
    countDescendants(node);
    return count;
  }

  getOrgPath(org: mjBizAppsCommonOrganizationEntity): string {
    const path: string[] = [];
    let current: mjBizAppsCommonOrganizationEntity | undefined = org;

    while (current) {
      path.unshift(current.Name);
      const parentId = current.ParentID;
      current = parentId ? this.organizations.find(o => o.ID === parentId) : undefined;
    }

    return path.join(' → ');
  }

  isSelected(org: mjBizAppsCommonOrganizationEntity): boolean {
    return this.selectedOrg?.ID === org.ID;
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadOrgSelectorComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
